import { FC, useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { Tooltip } from 'framework/ui/mui-builder';
import { IssueItem } from '../../types';

const SEVERITY_ORDER: Record<IssueItem['severity'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

interface IssuesSectionProps {
  issues: IssueItem[];
  maxItems?: number;
  showSeverityColors?: boolean;
  title?: string;
}

const IssuesSection: FC<IssuesSectionProps> = ({ issues, maxItems = 3, showSeverityColors = true, title }) => {
  const sortedIssues = useMemo(() => {
    return [...(issues || [])].sort((a, b) => {
      const severityDiff = (SEVERITY_ORDER[a.severity] ?? 4) - (SEVERITY_ORDER[b.severity] ?? 4);
      if (severityDiff !== 0) return severityDiff;
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  }, [issues]);

  if (!issues || !issues.length) {
    return (
      <Box>
        {title && (
          <Typography variant="subtitle2" color="textSecondary" mb={1}>
            {title} (0)
          </Typography>
        )}

        <Typography variant="body1" color="textSecondary" fontStyle="italic">
          No issues available
        </Typography>
      </Box>
    );
  }

  const displayLimit = maxItems;
  const hasMoreIssues = sortedIssues.length > displayLimit;
  const displayedIssues = sortedIssues.slice(0, displayLimit);
  const remainingCount = sortedIssues.length - displayLimit;
  const tooltipContent = (
    <Box>
      {sortedIssues.map((issue, index) => (
        <Box mb={2} key={issue.id || index}>
          {issue.description}
        </Box>
      ))}
    </Box>
  );

  const content = (
    <Box>
      {title && (
        <Typography variant="subtitle2" color="textSecondary" mb={1}>
          {title} ({issues.length})
        </Typography>
      )}

      <Stack>
        {displayedIssues.map((issue, index) => (
          <Typography key={issue.id || index} variant="body2" mb={1} color="error">
            {issue.description}
          </Typography>
        ))}

        {hasMoreIssues && (
          <Typography variant="body2" color="error">
            ...and {remainingCount} more
          </Typography>
        )}
      </Stack>
    </Box>
  );

  return hasMoreIssues ? (
    <Tooltip title={tooltipContent} arrow placement="top">
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default IssuesSection;
